/**
 * Decoupled bridge between the transport layer and the host app's auth
 * provider (Auth0) / store (Redux). This package must stay framework- and
 * store-agnostic, so the app registers callbacks here instead of the
 * transport importing Auth0/Redux directly.
 *
 * If no bridge is registered (e.g. an app that hasn't opted in),
 * requestFreshToken() throws immediately and the caller's original error
 * propagates unchanged.
 */

export type TokenRefresher = () => Promise<string>;
export type SessionExpiredHandler = () => void;
export type TokenRefreshedListener = (token: string) => void;

let refresher: TokenRefresher | null = null;
let sessionExpiredHandler: SessionExpiredHandler | null = null;
const tokenListeners = new Set<TokenRefreshedListener>();

/** Called once, at app root, to wire live Auth0/Redux callbacks into the transport layer. */
export function registerAuthBridge(opts: {
  refreshToken: TokenRefresher;
  onSessionExpired: SessionExpiredHandler;
}): () => void {
  refresher = opts.refreshToken;
  sessionExpiredHandler = opts.onSessionExpired;
  return () => {
    refresher = null;
    sessionExpiredHandler = null;
  };
}

/** Subscribe to be notified whenever the transport layer mints a fresh token. */
export function onTokenRefreshed(listener: TokenRefreshedListener): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

/** @internal used by transport.ts */
export async function requestFreshToken(): Promise<string> {
  if (!refresher) {
    throw new Error('No auth bridge registered; cannot refresh token');
  }
  const token = await refresher();
  tokenListeners.forEach((listener) => {
    listener(token);
  });
  return token;
}

/** @internal used by transport.ts */
export function reportSessionExpired(): void {
  sessionExpiredHandler?.();
}

const REAL_EXPIRY_MARKERS = ['login_required', 'consent_required', 'Missing Refresh Token'];
const REAL_EXPIRY_ERROR_CODES = ['invalid_grant', 'login_required', 'consent_required'];

/**
 * Single source of truth for classifying an Auth0 error as a genuine session
 * expiry (vs. a transient failure). A revoked/rotated refresh token arrives
 * as a `GenericError` with `error: 'invalid_grant'` and no matching
 * substring in `.message`, so the structured error code is checked first.
 * Used by transport.ts and useAuthClaims.ts.
 */
export function isGenuineSessionExpiryError(err: unknown): boolean {
  if (typeof err === 'object' && err !== null && 'error' in err) {
    const code = (err as { error?: unknown }).error;
    if (typeof code === 'string' && REAL_EXPIRY_ERROR_CODES.includes(code)) {
      return true;
    }
  }
  const message = err instanceof Error ? err.message : String(err);
  return REAL_EXPIRY_MARKERS.some((marker) => message.includes(marker));
}
